import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Button, Modal, Space, Tour, Typography, type TourProps } from "antd";
import { CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../helpers/api";
import { activeTutorial, installTutorial, uninstallTutorial } from "./engine/session";
import { matchPath, TUTORIAL_API_EVENT, type TutorialApiEventDetail } from "./engine/mockServer";
import type { StepTrigger, Tutorial, TutorialStep } from "./types";

/**
 * Runs a guide: keeps the current step, puts the app on the step's page, points the Tour
 * at the step's anchor once it is on screen, and watches for whatever completes a
 * do-it-yourself step. The pages themselves take no part beyond rendering `data-tour`
 * anchors - the driver only ever reads the document and the mock server's events.
 */

interface ActiveGuide {
  tutorial: Tutorial;
  index: number;
}

interface TutorialContextValue {
  active: ActiveGuide | null;
  start: (id: string) => void;
  restart: () => void;
  exit: () => void;
}

const TutorialContext = createContext<TutorialContextValue>({ active: null, start: () => {}, restart: () => {}, exit: () => {} });

export function useTutorial() {
  return useContext(TutorialContext);
}

/** Where the Tutorials page lives - the exit lands here. */
export const TUTORIALS_PATH = "/tutorials";

/** Marks the element an action step is waiting on, for the glow in index.css. */
const ACTIVE_ATTR = "data-tour-active";

/** A page's anchors are plain attributes, so a step target is one query away. */
function findTarget(id: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour="${id}"]`);
}

function onRoute(pattern: string, pathname: string): boolean {
  return matchPath(pattern, pathname) !== null;
}

/** Re-runs `test` whenever the document changes (and on a slow tick, for changes that are not DOM mutations). */
function watchDocument(test: () => boolean, onTrue: () => void): () => void {
  let done = false;
  const check = () => {
    if (done) return;
    if (test()) {
      done = true;
      stop();
      onTrue();
    }
  };
  const observer = new MutationObserver(check);
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
  const timer = window.setInterval(check, 300);
  const stop = () => {
    observer.disconnect();
    window.clearInterval(timer);
  };
  check();
  return stop;
}

function triggerTest(trigger: StepTrigger): (() => boolean) | null {
  if (trigger.kind === "dom") return () => document.querySelector(trigger.selector) !== null;
  if (trigger.kind === "check") return trigger.test;
  return null;
}

export function TutorialProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // A reload mid-guide comes back here with the guide already installed (session.ts).
  const [active, setActive] = useState<ActiveGuide | null>(() => {
    const t = activeTutorial();
    return t ? { tutorial: t, index: 0 } : null;
  });
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  // Observers registered in effects outlive a render; they read the guide through this.
  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const step: TutorialStep | undefined = active?.tutorial.steps[active.index];
  const onStepRoute = !!step && onRoute(step.route, location.pathname);

  // Only ever moves forward from the step it was asked to move from, so two observers
  // seeing the same change cannot skip a step between them.
  const advance = useCallback((from: number) => {
    const current = activeRef.current;
    if (!current || current.index !== from) return;
    const next = from + 1;
    if (next >= current.tutorial.steps.length) {
      setFinished(true);
      return;
    }
    setActive({ tutorial: current.tutorial, index: next });
  }, []);

  const start = useCallback(
    (id: string) => {
      // The guides and their sample data are lazy chunks: nothing moves until they are in
      // place, so no page asks the real API for a sample record in the meantime.
      import("./registry").then(async ({ findTutorial }) => {
        const tutorial = findTutorial(id);
        if (!tutorial) return;
        await installTutorial(tutorial);
        setFinished(false);
        setOpen(false);
        setActive({ tutorial, index: 0 });
        navigate(tutorial.steps[0].route);
        // Everything cached from the real API (the signed-in user above all) is re-read
        // from the sample data; on exit the same call reads it all back from the API.
        queryClient.resetQueries();
      });
    },
    [navigate, queryClient],
  );

  const restart = useCallback(() => {
    const current = activeRef.current;
    if (current) start(current.tutorial.id);
  }, [start]);

  const exit = useCallback(() => {
    uninstallTutorial();
    setFinished(false);
    setOpen(false);
    setActive(null);
    navigate(TUTORIALS_PATH);
    queryClient.resetQueries();
  }, [navigate, queryClient]);

  // 1. The step's page: navigate there when the app is anywhere else as the step starts.
  // Only as it starts: the user's own action is what moves the app to the next step's
  // page, and reacting to that move here (before the trigger below has advanced the
  // step) would pull them straight back. A pattern route (":id") cannot be navigated to
  // - the previous step's action is what lands on it. Keyed on the step alone: both the
  // location and `navigate` itself (under BrowserRouter) change on every navigation, and
  // either in the deps re-runs this for the old step mid-move.
  useEffect(() => {
    if (!step) return;
    // A persona change first: the sample server switches who /me answers with, and the
    // cached user (and everything else read as the old one) is re-read.
    const switching = step.actAs ? api.post("/tutorial/act-as", { userId: step.actAs }).then(() => queryClient.resetQueries()) : Promise.resolve();
    switching.then(() => {
      if (step.route.includes(":") || onRoute(step.route, window.location.pathname)) return;
      navigate(step.route);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // 2. The popover: shown once the step's anchor is on the page (a moment after, so the
  // layout has settled around whatever just loaded), centered when the step has none.
  useEffect(() => {
    setOpen(false);
    if (!step || !onStepRoute) return;
    let timer: number | undefined;
    const stop = watchDocument(
      () => !step.target || findTarget(step.target) !== null,
      () => {
        timer = window.setTimeout(() => setOpen(true), 350);
      },
    );
    return () => {
      stop();
      window.clearTimeout(timer);
    };
  }, [step, onStepRoute]);

  // 3. Keep the popover on its anchor: rc-tour re-measures the target on window resize,
  // so a page that reflows under the popover (a table finishing loading) gets one.
  useEffect(() => {
    if (!open) return;
    let timer: number | undefined;
    const observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => window.dispatchEvent(new Event("resize")), 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [open]);

  // 4. The glow on an action step's anchor (index.css), for as long as the step waits.
  useEffect(() => {
    if (!open || !step?.action || !step.target) return;
    const el = findTarget(step.target);
    el?.setAttribute(ACTIVE_ATTR, "");
    return () => el?.removeAttribute(ACTIVE_ATTR);
  }, [open, step]);

  // 5. What completes a do-it-yourself step.
  useEffect(() => {
    if (!active || !step?.action || !onStepRoute) return;
    const from = active.index;
    const trigger = step.action;
    switch (trigger.kind) {
      case "route":
        return; // handled below, off the location
      case "api": {
        const handler = (e: Event) => {
          const d = (e as CustomEvent<TutorialApiEventDetail>).detail;
          if (d.ok && d.method === trigger.method && matchPath(trigger.path, d.path)) advance(from);
        };
        window.addEventListener(TUTORIAL_API_EVENT, handler);
        return () => window.removeEventListener(TUTORIAL_API_EVENT, handler);
      }
      case "click": {
        const target = step.target;
        if (!target) return;
        const handler = (e: Event) => {
          if ((e.target as Element | null)?.closest(`[data-tour="${target}"]`)) advance(from);
        };
        document.addEventListener("click", handler, true);
        return () => document.removeEventListener("click", handler, true);
      }
      default: {
        const test = triggerTest(trigger);
        return test ? watchDocument(test, () => advance(from)) : undefined;
      }
    }
  }, [active, step, onStepRoute, advance]);

  useEffect(() => {
    if (!active || step?.action?.kind !== "route") return;
    if (onRoute(step.action.path, location.pathname)) advance(active.index);
  }, [active, step, location.pathname, advance]);

  const total = active?.tutorial.steps.length ?? 0;
  const index = active?.index ?? 0;
  const isLast = index === total - 1;

  const tourSteps: TourProps["steps"] = useMemo(() => {
    if (!step) return [];
    return [
      {
        title: step.title,
        description: step.body,
        // The types want a function that always finds something; a vanished anchor
        // centers the popover, which is the right fallback.
        target: step.target ? (() => findTarget(step.target!) as HTMLElement) : null,
        placement: step.placement,
        // An explaining step spotlights its anchor; a doing step leaves the whole page
        // usable, since the anchor may open a dropdown or a dialog the user has to reach.
        mask: !step.action,
      },
    ];
  }, [step]);

  const value = useMemo<TutorialContextValue>(() => ({ active, start, restart, exit }), [active, start, restart, exit]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {active && step && (
        <Tour
          open={open && !finished}
          steps={tourSteps}
          current={0}
          // The close icon is the only built-in control left (the actions below are
          // ours), so closing is unambiguously "leave the tutorial". rc-tour's own Finish
          // button would call onClose too, which is why it is not used.
          onClose={exit}
          disabledInteraction={false}
          // Below antd's Modal (1000) and dropdowns (1050): a dialog or picker a step asks
          // the user to open must float over the popover, never be covered by it.
          zIndex={step.aboveDialogs ? 1100 : 900}
          indicatorsRender={() => (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Step {index + 1} of {total}
            </Typography.Text>
          )}
          actionsRender={() =>
            step.action ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Do this on the page to continue
              </Typography.Text>
            ) : (
              <Button type="primary" size="small" onClick={() => advance(index)}>
                {isLast ? "Finish" : "Next"}
              </Button>
            )
          }
        />
      )}
      <Modal
        open={finished}
        title="Tutorial complete"
        closable={false}
        footer={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={restart}>
              Run it again
            </Button>
            <Button type="primary" onClick={exit}>
              Back to tutorials
            </Button>
          </Space>
        }
      >
        {active && (
          <p>
            That is the whole of <strong>{active.tutorial.title}</strong>. Nothing you did here was saved - the sample data is
            thrown away when you leave.
          </p>
        )}
      </Modal>
    </TutorialContext.Provider>
  );
}

/** The strip above the header while a guide runs: which guide, how far along, and the way out. */
export function TutorialBar() {
  const { active, restart, exit } = useTutorial();
  if (!active) return null;
  const { tutorial, index } = active;
  return (
    <div className="glut-tutorial-bar" role="status">
      <span className="glut-tutorial-bar-label">Tutorial</span>
      <span className="glut-tutorial-bar-title">{tutorial.title}</span>
      <span className="glut-tutorial-bar-meta">
        Step {index + 1} of {tutorial.steps.length} - nothing here is saved
      </span>
      <Space size={4} className="glut-tutorial-bar-actions">
        <Button size="small" icon={<ReloadOutlined />} onClick={restart}>
          Restart
        </Button>
        <Button size="small" icon={<CloseOutlined />} onClick={exit}>
          Exit tutorial
        </Button>
      </Space>
    </div>
  );
}
